import { ErrorFallback } from '@lighthouse/block'
import {
    ApplicationProvider,
    AuthenticationProvider,
    CollapseManager,
    DomainProvider,
    getDepartmentListWithPath,
    LanguageProvider,
    PAGE_CONTAINER_HOST,
    useAtomData
} from '@lighthouse/shared'
import { useAtom, useAtomValue } from 'jotai'
import React, { useCallback, useEffect, useMemo } from 'react'
import { ErrorBoundary } from 'react-error-boundary'
import useSWR from 'swr'

import { applicationSettingAtom, AppPublishStateAtom, isMobileDeviceAtom } from '@/atoms/application/state'
import { useAppLanguageList } from '@/hooks/useApplication'
import { useDataSourceDepartments, useDataSourceRoles, useDataSourceUsers } from '@/hooks/useDataSource'
import { checkVersionUpdated, fetchAVPlayUrl, fetchVideoCoverUrl, getVideoPlayAuth } from '@/services'

import ApplicationHeader from './ApplicationHeader'
import { PublishProgress } from './ApplicationHeader/Actions/PublishProgress'
import { ApplicationMessage } from './ApplicationMessage'
import * as SC from './styles'

interface ApplicationContainerProps {
    id: string
    children?: React.ReactNode
}

/** 轮询应用是否有更新 */
const useSubscriptApplicationUpdate = () => {
    return useSWR('query-app-has-update', () => checkVersionUpdated(), { refreshInterval: 3000 })
}

export const ApplicationContainer: React.FC<ApplicationContainerProps> = ({ id, children }) => {
    const appSetting = useAtomData(
        applicationSettingAtom,
        useCallback(s => ({ navbar: s?.navbar, domain: s?.domain, authentication: s?.authentication }), [])
    )
    const { navbar, domain, authentication } = appSetting
    const users = useDataSourceUsers()
    const roles = useDataSourceRoles()
    const departments = useDataSourceDepartments()
    const languageList = useAppLanguageList()
    // 管理端：跟随当前选中断点的 type 来决定 UI 样式
    const isMobile = useAtomValue(isMobileDeviceAtom)

    const hasUpdateSwrRes = useSubscriptApplicationUpdate()

    const departmentsWithPathLabel = useMemo(() => getDepartmentListWithPath(departments), [departments])

    const applicationValue = useMemo(
        () => ({
            appId: id,
            personOptions: users,
            roleOptions: roles,
            departmentOptions: departmentsWithPathLabel,
            isMobile,
            isBuilder: true,
            pageTarget: PAGE_CONTAINER_HOST,
            fetchVideoPlayAuth: getVideoPlayAuth,
            fetchVideoCoverUrl,
            fetchAVPlayUrl: fetchAVPlayUrl,
            hasUpdate: hasUpdateSwrRes.data,
            mutateHasUpdate: hasUpdateSwrRes.mutate
        }),
        [departmentsWithPathLabel, hasUpdateSwrRes.data, hasUpdateSwrRes.mutate, id, isMobile, roles, users]
    )

    const languageValue = useMemo(
        () => ({
            languageList
        }),
        [languageList]
    )

    useEffect(() => {
        const list = Object.keys(navbar?.linkList?.list ?? {})
        list.forEach(id => {
            const isExit = CollapseManager.getItemCollapse(id)
            isExit === null ? CollapseManager.setItemCollapse(id, false) : CollapseManager.clearItemCollapse(id)
        })
    }, [navbar?.linkList?.list])

    const [appPublishState, setAppPublishState] = useAtom(AppPublishStateAtom)

    if (!navbar || !domain || !authentication) {
        return null
    }

    return (
        <ErrorBoundary FallbackComponent={ErrorFallback}>
            <ApplicationMessage appId={id} />
            <ApplicationProvider value={applicationValue}>
                <LanguageProvider value={languageValue}>
                    <DomainProvider value={domain}>
                        <AuthenticationProvider value={authentication}>
                            <SC.PageContainer id="appContainer">
                                <ApplicationHeader appId={id} />
                                <SC.PageContent>{children}</SC.PageContent>
                                {/* {!isDataSource && <ApplicationFooter />} */}
                            </SC.PageContainer>
                        </AuthenticationProvider>
                    </DomainProvider>
                </LanguageProvider>

                {/* 应用发布/更新的弹窗 */}
                <PublishProgress
                    key={~~appPublishState.isSuccess}
                    isPublish={appPublishState.isPublish}
                    // inProgress={appPublishState.isProgress}
                    isComplete={appPublishState.isSuccess}
                    // onProgressEnd={() =>
                    //     setAppPublishState(draft => {
                    //         draft.isProgress = false
                    //         draft.isSuccess = true
                    //     })
                    // }
                    onClose={() => {
                        setAppPublishState({
                            isPublish: false,
                            // isProgress: false,
                            isSuccess: false
                        })
                    }}
                />
            </ApplicationProvider>
        </ErrorBoundary>
    )
}
